
import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/context/PermissionsContext";
import { Resource, PermissionType } from "@/hooks/rollenbeheer/useRolePermissions";
import { toast } from "sonner";

interface ProtectedRouteProps {
  children: JSX.Element;
  allowedRoles?: string[];
  requiredResource?: Resource;
  requiredPermission?: PermissionType;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles = [],
  requiredResource,
  requiredPermission = "read"
}) => {
  const { isAuthenticated, user, isValidating } = useAuth() as any;
  const { hasPermission, isLoading } = usePermissions();
  const location = useLocation();
  const [permissionChecked, setPermissionChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  
  // Debug logs for troubleshooting
  useEffect(() => {
    console.log("ProtectedRoute mounted for path:", location.pathname);
    console.log("Authentication state:", { isAuthenticated, userRole: user?.role });
    console.log("Required permissions:", { requiredResource, requiredPermission });
    console.log("Permissions loading state:", isLoading);
  }, [location.pathname, isAuthenticated, user, requiredResource, requiredPermission, isLoading]);
  
  // Effect to check permissions when route changes or permissions load
  useEffect(() => {
    if (isAuthenticated) {
      let access = true;
      
      // R27: management-bypass verwijderd — toegang loopt nu volledig via
      // rollenbeheer (allowedRoles + requiredResource permissions).

      // Check for role-based access
      if (allowedRoles.length > 0 && user) {
        const hasRole = allowedRoles.includes(user.role);
        console.log(`Role check for ${user.role} in ${allowedRoles.join(', ')}: ${hasRole}`);
        
        if (!hasRole) {
          console.log(`User with role ${user.role} does not have required role`);
          toast.error(`Je hebt geen toegang tot deze pagina met rol '${user.role}'`);
          access = false;
        }
      }
      
      // Check for permission-based access
      if (access && requiredResource && !isLoading) {
        const hasResourcePermission = hasPermission(requiredResource, requiredPermission);
        console.log(`Permission check for ${requiredResource}.${requiredPermission}: ${hasResourcePermission}`);
        
        if (!hasResourcePermission) {
          console.log(`User with role ${user?.role} does not have permission ${requiredResource}.${requiredPermission}`);
          toast.error(`Je hebt onvoldoende rechten voor deze pagina`);
          access = false;
        }
      }
      
      setHasAccess(access);
      setPermissionChecked(true);
    }
  }, [
    location.pathname, 
    isLoading, 
    isAuthenticated, 
    allowedRoles, 
    user, 
    requiredResource, 
    requiredPermission, 
    hasPermission
  ]);
  
  // Wacht tot eerste sessie-validatie klaar is om bounce naar /login te
  // voorkomen direct na een succesvolle login (state nog niet gecommit).
  if (isValidating && !isAuthenticated) {
    return (
      <div className="flex justify-center items-center h-[50vh]">
        <p className="text-muted-foreground">Sessie controleren...</p>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    console.log("[login-flow] ProtectedRoute → redirect /login (not authed)");
    return <Navigate to="/login" replace />;
  }
  
  // Show loading indicator while permissions are being loaded - but with timeout
  if (isLoading && requiredResource && !permissionChecked) {
    console.log("Showing loading state for permission check");
    // Set a maximum loading time to prevent infinite loading
    setTimeout(() => {
      if (isLoading && !permissionChecked) {
        console.warn("Permission check timeout - proceeding with access");
        setPermissionChecked(true);
        setHasAccess(true);
      }
    }, 3000);
    
    return (
      <div className="flex justify-center items-center h-[50vh]">
        <p className="text-muted-foreground">Toegangsrechten laden...</p>
      </div>
    );
  }
  
  // Only redirect if permission check is complete and access is denied
  if (permissionChecked && !hasAccess) {
    console.log("Permission check failed, redirecting to dashboard");
    return <Navigate to="/dashboard" replace />;
  }
  
  // If we're still checking permissions or have access, render children
  console.log("Rendering protected route children");
  return children;
};

export default ProtectedRoute;
